import { useCallback, useMemo } from 'react';
import { useDailyState, usePersistentState } from '../../lib/storage';
import {
  canAddHealthField,
  createId,
  DEFAULT_HEALTH_FIELDS,
  healthCompletion,
  isUnknownArray,
  MAX_FIELD_LABEL,
  migrateHealthChecked,
  migrateHealthFields,
  pruneChecked,
  toggleChecked,
  type HealthCompletion,
  type HealthField,
} from '../../lib/dailies';

/** Module-level so the hooks' initial values keep a stable identity. */
const NO_IDS: readonly unknown[] = [];

export interface HealthState {
  readonly fields: readonly HealthField[];
  readonly checkedIds: readonly string[];
  readonly completion: HealthCompletion;
  readonly canAdd: boolean;
  readonly isChecked: (id: string) => boolean;
  readonly toggle: (id: string) => void;
  readonly addField: (label: string) => void;
  readonly renameField: (id: string, label: string) => void;
  readonly removeField: (id: string) => void;
  readonly restoreDefaults: () => void;
}

/**
 * The two halves of Daily Health, deliberately on different lifetimes:
 *
 *   `health:fields`  usePersistentState — the user configured these once and
 *                    they must survive every rollover. Putting them on
 *                    useDailyState would silently wipe the user's setup every
 *                    midnight, which is the data-loss bug this split prevents.
 *   `health:checked` useDailyState      — today's ticks only. `useDailyState`
 *                    already subscribes to the shared midnight ticker, so the
 *                    reset happens live in a pinned tab. No local timer here.
 *
 * Both are read through a coarse "is it an array" guard and then salvaged by
 * a pure migrate pass, so one corrupt row costs one row, not the whole panel,
 * and a totally corrupt blob is deleted by readJson and degrades to the
 * fallback instead of white-screening a `.map()`.
 */
export function useHealth(): HealthState {
  const [rawFields, setRawFields] = usePersistentState<readonly unknown[]>(
    'health',
    'fields',
    DEFAULT_HEALTH_FIELDS,
    isUnknownArray,
  );
  const [rawChecked, setRawChecked] = useDailyState<readonly unknown[]>(
    'health',
    'checked',
    NO_IDS,
    isUnknownArray,
  );

  const fields = useMemo(() => migrateHealthFields(rawFields), [rawFields]);
  const checkedIds = useMemo(() => migrateHealthChecked(rawChecked), [rawChecked]);

  const completion = useMemo(
    () => healthCompletion(fields, checkedIds),
    [fields, checkedIds],
  );

  const isChecked = useCallback((id: string) => checkedIds.includes(id), [checkedIds]);

  const toggle = useCallback(
    (id: string) => setRawChecked(toggleChecked(checkedIds, id)),
    [checkedIds, setRawChecked],
  );

  const addField = useCallback(
    (label: string) => {
      const clean = label.trim().slice(0, MAX_FIELD_LABEL);
      if (clean === '' || !canAddHealthField(fields)) return;
      setRawFields([...fields, { id: createId('health'), label: clean }]);
    },
    [fields, setRawFields],
  );

  const renameField = useCallback(
    (id: string, label: string) => {
      setRawFields(
        fields.map((f) => (f.id === id ? { ...f, label: label.slice(0, MAX_FIELD_LABEL) } : f)),
      );
    },
    [fields, setRawFields],
  );

  const removeField = useCallback(
    (id: string) => {
      const next = fields.filter((f) => f.id !== id);
      setRawFields(next);
      // Drop the now-orphaned tick so the ratio can't read 3/2.
      setRawChecked(pruneChecked(checkedIds, next));
    },
    [fields, checkedIds, setRawFields, setRawChecked],
  );

  const restoreDefaults = useCallback(
    () => setRawFields(DEFAULT_HEALTH_FIELDS),
    [setRawFields],
  );

  return {
    fields,
    checkedIds,
    completion,
    canAdd: canAddHealthField(fields),
    isChecked,
    toggle,
    addField,
    renameField,
    removeField,
    restoreDefaults,
  };
}
